/**
 * CheckoutSuccess — landing target after Stripe redirects back with
 * ?session_id=… Polls /api/checkout/status until payment_status becomes
 * 'paid' (or 5 attempts pass) and shows a confirmation card.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import BrandMark from '../components/BrandMark';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

const CheckoutSuccess = () => {
  const { API } = useContext(AuthContext);
  const { clear } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | expired | error
  const [txn, setTxn] = useState(null);
  const attempts = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }

    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= 6) {
        if (!cancelled) setState('expired');
        return;
      }
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setTxn(res.data);
        if (res.data.payment_status === 'paid') {
          setState('paid');
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          return;
        }
        if (res.data.status === 'expired') { setState('expired'); return; }
        setTimeout(poll, 2000);
      } catch (_) {
        if (!cancelled) { setState('error'); }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [API, sessionId, clear]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="checkout-success-page">
      <header className="border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="lg" onClick={() => navigate('/')} />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full">
          {state === 'polling' && (
            <div className="pro-card p-10 text-center" data-testid="checkout-polling">
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-5" />
              <h1 className="font-heading text-2xl font-extrabold text-foreground tracking-tight">
                Confirming your payment…
              </h1>
              <p className="mt-3 text-foreground/70">Sit tight — this usually only takes a couple of seconds.</p>
            </div>
          )}
          {state === 'paid' && (
            <div className="pro-card p-10 text-center" data-testid="checkout-paid">
              <div className="h-14 w-14 rounded-full bg-primary/10 text-primary mx-auto mb-5 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-foreground tracking-tight">
                Payment received. <span className="text-amber-brand">You&apos;re in.</span>
              </h1>
              {txn?.amount_cents != null && (
                <p className="mt-3 text-foreground/70">
                  Charged <span className="font-extrabold text-foreground">{money(txn.amount_cents)}</span>
                  {txn.currency && <span className="uppercase"> {txn.currency}</span>}.
                </p>
              )}
              {(txn?.items || []).length > 0 && (
                <ul className="mt-6 text-left space-y-2 border-t border-border pt-6">
                  {txn.items.map((it, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-foreground/80">
                        {it.event_title || it.product_name} × {it.quantity}
                      </span>
                      <span className="font-extrabold text-foreground">
                        {money((it.unit_amount_cents || 0) * (it.quantity || 1))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Button
                size="lg"
                onClick={() => navigate('/dashboard')}
                className="mt-8 rounded-full px-7 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="checkout-go-dashboard"
              >
                Go to dashboard <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}
          {(state === 'expired' || state === 'error') && (
            <div className="pro-card p-10 text-center" data-testid="checkout-error">
              <div className="h-14 w-14 rounded-full bg-destructive/10 text-destructive mx-auto mb-5 flex items-center justify-center">
                <XCircle className="w-7 h-7" />
              </div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground tracking-tight">
                We couldn&apos;t confirm your payment.
              </h1>
              <p className="mt-3 text-foreground/70">
                {state === 'expired'
                  ? 'The session expired before payment completed.'
                  : 'Something went wrong while confirming with Stripe.'} If you were charged, please email support with your session ID.
              </p>
              {sessionId && <p className="mt-4 text-xs text-foreground/50 break-all">Session: {sessionId}</p>}
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/shop')}
                className="mt-8 rounded-full px-7 font-bold"
                data-testid="checkout-back-to-shop"
              >
                Back to shop
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CheckoutSuccess;
